import React, { ChangeEvent, useState, useEffect, useRef } from 'react';
import { Label, FormControl, Select } from '@uhg-harmony/uitk-react';
// Replace theme import to shared theme
import { defaultTheme as theme } from 'shared/theme/theme';
import styled from 'styled-components';
import { getCombinedFontsForTheme } from 'builder/util/getCombinedFonts';
import {
  allowedKeys,
  characterSpacingValues,
  lineSpacingValues,
  widgetTypes,
} from 'builder/util/constants';
import {
  StyledClose,
  StyledPickerHeader,
  StyledPickerPopover,
  StyledPickerWrapper,
} from 'builder/components/GradientColorPicker/GradientColorPicker';
import GradientColorPicker from 'builder/components/GradientColorPicker/GradientColorPicker';
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconHighlight,
  IconItalic,
  IconPalette,
  IconStrikethrough,
  IconUnderline,
} from '@tabler/icons-react';
import { toast } from 'react-toastify';

export const MainLabelStyles = styled(Label)`
  color: #1d1929;
  font-size: 14px;
  font-family: OptumSans;
  font-weight: 500;
  padding-right: 0.5rem;
`;

export const FontSizeUnitStyle = styled(Label)`
  margin-left: 0.25rem;
  color: #1d1929;
  font-size: 14px;
  font-family: OptumSans;
  font-weight: 500;
  padding-right: 0.5rem;
`;

export const SmallLabelStyles = styled(Label)`
  color: #1d1929;
  font-size: 12px;
  font-family: OptumSans;
  font-weight: 500;
  padding-right: 0.5rem;
`;

const BaseSelect = styled(Select)`
  font-family: OptumSans;
  line-height: 1.25rem;
  border-radius: 0.375rem;
  & > select {
    font-family: OptumSans;
    line-height: 1.25rem;
  }
`;

export const FontSelect = styled(BaseSelect)`
  width: 9.375rem;
  margin-left: 0.6rem;
`;

export const FontSizeSelect = styled(BaseSelect)`
  width: 5.5rem;
  padding: 0.5rem;
`;

export const LineSpacingSelect = styled(BaseSelect)`
  width: 5.1rem;
  padding: 0.5rem;
`;

const StyledWrapper = styled.div`
  display: flex;
  align-items: baseline;
`;

const StyledTextWrapper = styled.div`
  display: flex;
  align-items: baseline;
`;

export const StyledSpacingWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const StyledAlignWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5em 0;
`;

const AlignmentIconWrapper = styled.span`
  display: flex;
  flex-direction: row;
  padding: 0 0.5rem 0 0;
`;

export const StyleWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 0.5rem;
`;

export const TextIconWrapper = styled.span`
  cursor: pointer;
  font-family: 'OptumSans';
  text-align: center;
  padding: 5px 5px 5px 0;
  border-radius: 7px;
  & :hover {
    background: ${theme.colors.aux.skyBlue};
  }
`;

const PaletteIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  text-align: center;
`;

export const StyledIcon = styled.div<{ selected: boolean; disabled: boolean }>`
  border-radius: 9px;
  height: 1.5em;
  background: ${props =>
    props.selected && !props.disabled ? theme.colors.aux.skyBlue : ''};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'default')};
  color: ${props =>
    props.disabled ? `${theme.colors.aux.lightGrey}` : 'inherit'};
  & :hover {
    background: ${props =>
      props.disabled
        ? theme.colors.aux.lightestGrey
        : theme.colors.aux.skyBlue};
    border-radius: 9px;
  }
`;

export const FontSizeInputWrapper = styled.div`
  display: inline-flex;
  margin-left: 0.5rem;
  align-items: baseline;
`;

export const FontSizeInput = styled.input`
  width: 4.5rem;
  height: 2.3rem;
  border-radius: 5px;
  font-size: 1rem;
  padding-left: 0.75rem;
  border: 1px solid #4b4d4f;
  color: #4b4d4f;
  font-family: OptumSans;
`;

export const handleNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (!allowedKeys.has(e.key)) {
    e.preventDefault();
  }
};

export interface TypographyPanelProps<T> {
  boldData: (data: Boolean) => void;
  centerAlignData?: (data: string) => void;
  currentTab?: number;
  disabled?: boolean;
  handleFontSize: (data: string) => void;
  handleCharacterSpacing?: (data: string) => void;
  handleFont: (data: string) => void;
  handleItalicData: (data: Boolean) => void;
  handleLineSpacing?: (data: string) => void;
  handleStrikeThrough: (data: Boolean) => void;
  handleTextHighlightColor?: (data: string) => void;
  handleTextColor?: (data: string) => void;
  handleUnderLine: (data: Boolean) => void;
  leftAlignData?: (data: string) => void;
  pages: Array<Page>;
  rightAlignData?: (data: string) => void;
  hideAlign?: boolean;
  hideSpacing?: boolean;
  hideTextColor?: boolean;
  hideTextHighlight?: boolean;
  site?: Site;
  updateSite?: (site: Site) => void;
  widgetState: Widget<T>;
  saveSite?: () => void;
  handleSignInLinkColor?: (data: string) => void;
}

export function getBold(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.bold;
    case 1:
      return widgetState.config.hoverData?.bold;
    default:
  }
}

export function getItalic(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.italic;
    case 1:
      return widgetState.config.hoverData?.italic;
    default:
  }
}

export function getUnderline(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.underline;
    case 1:
      return widgetState.config.hoverData?.underline;
    default:
  }
}

export function getStrikeThrough(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.strikeThrough;
    case 1:
      return widgetState.config.hoverData?.strikeThrough;
    default:
  }
}

export function getTextColor(widgetState: Widget<any>, tab: number): string {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.textColor;
    case 1:
      return widgetState.config.hoverData?.textColor;
    default:
  }
}

export function getFontFamily(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.font;
    case 1:
      return widgetState.config.hoverData?.font;
    default:
  }
}

export function getWidgetTypeDefaultFontSize(widgetState: Widget<any>) {
  if (widgetState.type) {
    switch (widgetState.type) {
      case widgetTypes.Text:
        return 16;
    }
  }
}

export function getFontSize(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return (
        widgetState.config.fontSize ?? getWidgetTypeDefaultFontSize(widgetState)
      );
    case 1:
      return (
        widgetState.config.hoverData?.fontSize ??
        getWidgetTypeDefaultFontSize(widgetState)
      );
    default:
  }
}

export function getAlign(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.align;
    case 1:
      return widgetState.config.hoverData?.align;
    default:
  }
}

export function getLineSpacing(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.lineSpacing;
    case 1:
      return widgetState.config.hoverData?.lineSpacing;
    default:
  }
}

export function getCharacterSpacing(widgetState: Widget<any>, tab: number) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.characterSpacing;
    case 1:
      return widgetState.config.hoverData?.characterSpacing;
    default:
  }
}

export const Typography: React.FC<TypographyPanelProps<any>> = (
  typographyPanelProps: TypographyPanelProps<any>
) => {
  const {
    boldData,
    disabled,
    handleFontSize,
    handleFont,
    handleItalicData,
    handleUnderLine,
    handleStrikeThrough,
    handleTextColor,
    handleTextHighlightColor,
    leftAlignData,
    centerAlignData,
    rightAlignData,
    handleLineSpacing,
    handleCharacterSpacing,
    handleSignInLinkColor,
    hideAlign,
    hideTextHighlight,
    widgetState,
    currentTab,
    site,
    updateSite,
    saveSite,
    hideTextColor,
    hideSpacing,
  } = typographyPanelProps;

  const [fontFamily, setFontFamily] = useState(
    getFontFamily(widgetState, currentTab)
  );
  const [fontSize, setFontSize] = useState(
    getFontSize(widgetState, currentTab)
  );
  const [lineSpacing, setLineSpacing] = useState(
    getLineSpacing(widgetState, currentTab)
  );
  const [characterSpacing, setCharacterSpacing] = useState(
    getCharacterSpacing(widgetState, currentTab)
  );
  const [bold, setBold] = useState(getBold(widgetState, currentTab));
  const [italic, setItalic] = useState(getItalic(widgetState, currentTab));
  const [underLine, setUnderLine] = useState(
    getUnderline(widgetState, currentTab)
  );
  const [strikeThrough, setStrikeThrough] = useState(
    getStrikeThrough(widgetState, currentTab)
  );
  const [textColor, setTextColor] = useState(
    getTextColor(widgetState, currentTab)
  );
  const [textHighlightColor, setTextHighlightColor] = useState(
    widgetState.config?.textHighlightColor
  );
  const [align, setAlign] = useState(getAlign(widgetState, currentTab));
  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLDivElement | null>(
    null
  );
  const [_color, setColor] = useState(widgetState.config?.iconBackgroundColor);
  const [signInLinkColor, setSignInLinkColor] = useState(
    widgetState.config.signInLinkColor
  );

  const textColorRef = useRef<HTMLDivElement>(null);
  const textHighlightColorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    boldData(bold);
  }, [bold]);

  useEffect(() => {
    handleFontSize(fontSize);
  }, [fontSize]);

  useEffect(() => {
    handleFont(fontFamily);
  }, [fontFamily]);

  useEffect(() => {
    if (handleLineSpacing) {
      handleLineSpacing(lineSpacing);
    }
  }, [lineSpacing]);

  useEffect(() => {
    if (handleCharacterSpacing) {
      handleCharacterSpacing(characterSpacing);
    }
  }, [characterSpacing]);

  useEffect(() => {
    handleItalicData(italic);
  }, [italic]);

  useEffect(() => {
    handleUnderLine(underLine);
  }, [underLine]);

  useEffect(() => {
    handleStrikeThrough(strikeThrough);
  }, [strikeThrough]);

  useEffect(() => {
    if (
      handleTextColor ||
      (textColor &&
        widgetState.type !== widgetTypes.LoginButton &&
        !(textColor.startsWith('linear') || textColor.startsWith('radial')))
    ) {
      handleTextColor(textColor);
    }
  }, [textColor]);

  useEffect(() => {
    if (handleTextHighlightColor) {
      handleTextHighlightColor(textHighlightColor);
    }
  }, [textHighlightColor]);

  useEffect(() => {
    if (align) {
      switch (align) {
        case 'left':
          leftAlignData(align);
          break;
        case 'right':
          rightAlignData(align);
          break;
        case 'center':
          centerAlignData(align);
          break;
        default:
      }
    }
  }, [align]);

  useEffect(() => {
    setItalic(getItalic(widgetState, currentTab));
    setFontFamily(getFontFamily(widgetState, currentTab));
    setFontSize(getFontSize(widgetState, currentTab));
    setLineSpacing(getLineSpacing(widgetState, currentTab));
    setCharacterSpacing(getCharacterSpacing(widgetState, currentTab));
    setBold(getBold(widgetState, currentTab));
    setUnderLine(getUnderline(widgetState, currentTab));
    setStrikeThrough(getStrikeThrough(widgetState, currentTab));
    setTextColor(getTextColor(widgetState, currentTab));
    setTextHighlightColor(widgetState.config.textHighlightColor);
    setAlign(getAlign(widgetState, currentTab));
    setColor(widgetState.config.iconBackgroundColor);
  }, [widgetState.config]);

  const pickerOpen = Boolean(pickerAnchorEl);
  const pickerId = pickerOpen ? 'simple-popover' : undefined;

  const handleBoldChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setBold(!bold);
    setColor(theme.colors.aux.skyBlue);
  };

  const handleItalicChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setItalic(!italic);
    setColor(theme.colors.aux.skyBlue);
  };

  const handleUnderLineChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setUnderLine(!underLine);
    setColor(theme.colors.aux.skyBlue);
  };

  const handleStrikeThroughChange = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setStrikeThrough(!strikeThrough);
    setColor(theme.colors.aux.skyBlue);
  };

  const handleLeftAlign = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setAlign('left');
    setColor(theme.colors.aux.skyBlue);
  };

  const handleCenterAlign = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setAlign('center');
    setColor(theme.colors.aux.skyBlue);
  };

  const handleRightAlign = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    setAlign('right');
    setColor(theme.colors.aux.skyBlue);
  };

  const handleFontChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setFontFamily(e.target.value);
  };

  const handleFontSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setFontSize(e.target.value);
  };

  const handleLineSpacingChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setLineSpacing(e.target.value);
  };

  const handleSpacing = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setCharacterSpacing(e.target.value);
  };

  const handleColorEditClick = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    event.preventDefault();
    setPickerAnchorEl(event.currentTarget);
    setColor(theme.colors.aux.skyBlue);
  };

  const handlePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
  };

  const handlePickerClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setPickerAnchorEl(null);
  };

  const handleColorChange = newColor => {
    if (pickerAnchorEl === textColorRef.current) {
      if (
        newColor &&
        (newColor.startsWith('linear') || newColor.startsWith('radial'))
      ) {
        toast.dismiss();
        toast('Gradient cannot be used for text color', {
          type: 'warning',
          theme: 'colored',
        });
      } else if (widgetState.type === widgetTypes.LoginButton) {
        setSignInLinkColor(newColor);
        if (handleSignInLinkColor) {
          handleSignInLinkColor(newColor);
        }
      } else {
        setTextColor(newColor);
      }
    }
    if (pickerAnchorEl === textHighlightColorRef.current) {
      if (newColor.startsWith('linear') || newColor.startsWith('radial')) {
        toast('Gradient cannot be used for text color', {
          type: 'warning',
          theme: 'colored',
        });
      } else {
        setTextHighlightColor(newColor);
      }
    }
  };

  return (
    <>
      <StyledWrapper>
        <MainLabelStyles>Font</MainLabelStyles>
        <FormControl
          disabled={disabled}
          data-test-id="font-select-dropdown"
          id="controlled-select"
        >
          <FontSelect
            onChange={handleFontChange}
            value={fontFamily}
            data-test-id="font-select"
          >
            {Object.keys(getCombinedFontsForTheme()).map(f => {
              return (
                <option value={f} key={f}>
                  {f}
                </option>
              );
            })}
          </FontSelect>
        </FormControl>
        <FontSizeInputWrapper>
          <FontSizeInput
            id="typography-font-size"
            value={fontSize}
            onChange={handleFontSizeChange}
            onKeyDown={handleNumberInput}
            data-test-id="typography-font-size"
            type="number"
            min={0}
          />
          <FontSizeUnitStyle>px</FontSizeUnitStyle>
        </FontSizeInputWrapper>
      </StyledWrapper>
      <StyleWrapper>
        <MainLabelStyles>Style</MainLabelStyles>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={bold}
            data-test-id="bold-icon"
            title="Bold"
            onClick={e => {
              !disabled && handleBoldChange(e);
            }}
          >
            <IconBold />
          </StyledIcon>
        </TextIconWrapper>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={italic}
            data-test-id="italic-icon"
            title="Italic"
            onClick={e => {
              !disabled && handleItalicChange(e);
            }}
          >
            <IconItalic />
          </StyledIcon>
        </TextIconWrapper>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={underLine}
            data-test-id="underline-icon"
            title="Underline"
            onClick={e => {
              !disabled && handleUnderLineChange(e);
            }}
          >
            <IconUnderline />
          </StyledIcon>
        </TextIconWrapper>
        <TextIconWrapper>
          <StyledIcon
            disabled={disabled}
            selected={strikeThrough}
            data-test-id="strikethrough-icon"
            title="Strikethrough"
            onClick={e => {
              !disabled && handleStrikeThroughChange(e);
            }}
          >
            <IconStrikethrough />
          </StyledIcon>
        </TextIconWrapper>
        {!hideTextColor && (
          <PaletteIconWrapper>
            <MainLabelStyles>Color</MainLabelStyles>
            <TextIconWrapper>
              <StyledIcon
                disabled={disabled}
                ref={textColorRef}
                selected={pickerAnchorEl === textColorRef.current}
                id="text-color"
                data-test-id="text-color-icon"
                onClick={e => {
                  !disabled && handleColorEditClick(e);
                }}
              >
                <IconPalette size={30} />
              </StyledIcon>
            </TextIconWrapper>
            {!hideTextHighlight && (
              <TextIconWrapper>
                <StyledIcon
                  disabled={disabled}
                  ref={textHighlightColorRef}
                  selected={pickerAnchorEl === textHighlightColorRef.current}
                  data-test-id="text-highlight-color-icon"
                  id="text-highlight-color"
                  onClick={e => {
                    !disabled && handleColorEditClick(e);
                  }}
                >
                  <IconHighlight size={30} />
                </StyledIcon>
              </TextIconWrapper>
            )}
          </PaletteIconWrapper>
        )}
      </StyleWrapper>
      <StyledPickerPopover
        data-test-id="widget-color-picker-popover"
        id={pickerId}
        open={pickerOpen}
        onClick={handlePickerClick}
        onClose={handlePickerClose}
        anchorEl={pickerAnchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
      >
        <StyledPickerWrapper>
          <StyledPickerHeader data-test-id="styled-picker-header">
            Color Selector
          </StyledPickerHeader>
          <StyledClose data-test-id="close-icon" onClick={handlePickerClose} />
        </StyledPickerWrapper>
        <GradientColorPicker
          site={site}
          handleColorChange={handleColorChange}
          updateSite={updateSite}
          saveSite={saveSite}
          anchorEl={pickerAnchorEl}
          value={
            widgetState.type === widgetTypes.LoginButton
              ? signInLinkColor
              : textColor
          }
          onChange={handleColorChange}
        />
      </StyledPickerPopover>
      {!hideAlign && (
        <StyledAlignWrapper>
          <MainLabelStyles children="Alignment" />
          <AlignmentIconWrapper>
            <TextIconWrapper>
              <StyledIcon
                disabled={disabled}
                selected={align === 'left'}
                data-test-id="left-align-icon"
                title="Align Left"
                onClick={e => {
                  !disabled && handleLeftAlign(e);
                }}
              >
                <IconAlignLeft />
              </StyledIcon>
            </TextIconWrapper>
            <TextIconWrapper>
              <StyledIcon
                disabled={disabled}
                selected={align === 'center'}
                data-test-id="center-align-icon"
                title="Align Center"
                onClick={e => {
                  !disabled && handleCenterAlign(e);
                }}
              >
                <IconAlignCenter />
              </StyledIcon>
            </TextIconWrapper>
            <TextIconWrapper>
              <StyledIcon
                disabled={disabled}
                selected={align === 'right'}
                data-test-id="right-align-icon"
                title="Align Right"
                onClick={e => {
                  !disabled && handleRightAlign(e);
                }}
              >
                <IconAlignRight />
              </StyledIcon>
            </TextIconWrapper>
          </AlignmentIconWrapper>
        </StyledAlignWrapper>
      )}
      {!hideSpacing && (
        <StyledTextWrapper>
          <MainLabelStyles>Spacing</MainLabelStyles>
          <StyledSpacingWrapper>
            <FormControl disabled={disabled} id="line-spacing-select">
              <LineSpacingSelect
                onChange={handleLineSpacingChange}
                value={lineSpacing}
                data-test-id="line-spacing-dropdown"
              >
                {lineSpacingValues.map(f => (
                  <option value={f}>{f}</option>
                ))}
              </LineSpacingSelect>
            </FormControl>
            <SmallLabelStyles>Line</SmallLabelStyles>
          </StyledSpacingWrapper>
          <StyledSpacingWrapper>
            <FormControl disabled={disabled} id="character-spacing-select">
              <LineSpacingSelect
                onChange={handleSpacing}
                value={characterSpacing}
                data-test-id="character-spacing-dropdown"
              >
                {characterSpacingValues.map(f => (
                  <option value={f}>{f}</option>
                ))}
              </LineSpacingSelect>
            </FormControl>
            <SmallLabelStyles>Character</SmallLabelStyles>
          </StyledSpacingWrapper>
        </StyledTextWrapper>
      )}
    </>
  );
};
